import detailMessage from './detailMessage';
import React, { useState, FormEvent, ChangeEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { performPasswordReset } from "./api";
import ConfirmShell from "./components/ConfirmShell";
import { useLang } from "./LangContext";

type Status = "idle" | "ok" | "error";

interface PageState {
  status: Status;
  message?: string;
}

export default function PasswordResetPage() {
  const { t } = useLang();
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [state, setState] = useState<PageState>({ status: "idle" });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (password.length < 8) {
      setState({ status: "error", message: t.passwordTooShort });
      return;
    }
    if (password !== confirm) {
      setState({ status: "error", message: t.passwordsNoMatch });
      return;
    }
    setSubmitting(true);
    setState({ status: "idle" });
    try {
      await performPasswordReset(token!, password);
      setState({ status: "ok" });
    } catch (err) {
      setState({ status: "error", message: detailMessage(err, t.somethingWentWrong) });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ConfirmShell>
      {state.status === "ok" ? (
        <>
          <div className="confirm-status-icon ok" aria-hidden="true">✓</div>
          <h2>{t.passwordUpdatedTitle}</h2>
          <p>{t.passwordUpdatedMessage}</p>
          <p className="close-tab-hint">
            <Link to="/">{t.backToLogin}</Link>
          </p>
        </>
      ) : (
        <>
          <h2>{t.chooseNewPassword}</h2>
          <form onSubmit={submit} style={{ marginTop: "1rem", display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <input
              type="password"
              required
              placeholder={t.newPasswordPlaceholder}
              value={password}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
            />
            <input
              type="password"
              required
              placeholder={t.confirmNewPasswordPlaceholder}
              value={confirm}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
            />
            {state.status === "error" && (
              <div className="error-message">{state.message}</div>
            )}
            <button type="submit" disabled={submitting}>
              {submitting ? t.updatingPassword : t.updatePasswordButton}
            </button>
          </form>
        </>
      )}
    </ConfirmShell>
  );
}
